import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { ToastController } from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../services/panel-host.service';
import { TripFinanceService } from '../../../services/trip-finance.service';
import { TripService } from '../../../services/trip.service';
import { ConfirmationService } from '../../../services/confirmation.service';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';
import { ButtonComponent } from '../../ui/button/button.component';

@Component({
  selector: 'app-fundraising-profile-panel',
  templateUrl: './fundraising-profile-panel.component.html',
  imports: [
    TranslocoDirective,
    ContentContainerComponent,
    PanelBodyComponent,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FundraisingProfilePanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);

  readonly submitting = signal(false);

  readonly context = this.panelHost.fundraisingProfilePanelContext;
  readonly hasEnded = computed(() => this.tripService.currentTrip.value()?.hasEnded ?? false);

  readonly photoUrl = computed(() => {
    const panelContext = this.context();
    if (!panelContext) {
      return null;
    }
    return panelContext.version === 'submitted'
      ? panelContext.profile.submittedPhoto
      : panelContext.profile.approvedPhoto;
  });

  readonly description = computed(() => {
    const panelContext = this.context();
    if (!panelContext) {
      return null;
    }
    return panelContext.version === 'submitted'
      ? panelContext.profile.submittedDescription
      : panelContext.profile.approvedDescription;
  });

  readonly showActions = computed(() => {
    const panelContext = this.context();
    if (!panelContext || this.hasEnded()) {
      return false;
    }
    return panelContext.version === 'submitted' && panelContext.profile.submittedId !== null;
  });

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'fundraising-profile') {
        this.submitting.set(false);
      }
    });
  }

  approve(): void {
    void this.submitDecision(true);
  }

  async reject(): Promise<void> {
    const panelContext = this.context();
    if (!panelContext || this.submitting()) {
      return;
    }

    const confirmed = await this.confirmationService.confirm({
      header: this.transloco.translate(
        marker('trip-finances.fundraising-profile-panel.reject-confirm-header'),
      ),
      message: this.transloco.translate(
        marker('trip-finances.fundraising-profile-panel.reject-confirm-message'),
        { name: panelContext.profile.name },
      ),
      cancelText: this.transloco.translate(
        marker('trip-finances.fundraising-profile-panel.reject-confirm-cancel'),
      ),
      confirmText: this.transloco.translate(
        marker('trip-finances.fundraising-profile-panel.reject-confirm-confirm'),
      ),
      confirmRole: 'confirm',
    });

    if (!confirmed) {
      return;
    }

    await this.submitDecision(false);
  }

  private async submitDecision(approved: boolean): Promise<void> {
    const tripId = this.tripService.currentTrip.value()?.id;
    const panelContext = this.context();
    const profileId = panelContext?.profile.submittedId;

    if (!tripId || profileId == null || !panelContext || this.submitting()) {
      return;
    }

    const participantName = panelContext.profile.name;

    this.submitting.set(true);
    this.tripFinanceService
      .approveOrRejectProfile(tripId, profileId, { approved })
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe({
        next: async () => {
          this.tripFinanceService.moneyIn.reload();
          await this.presentActionToast(approved, participantName);
          await this.panelHost.dismiss();
        },
        error: async () => {
          const toast = await this.toastController.create({
            message: this.transloco.translate(marker('trip-finances.panels.save-error')),
            duration: 4000,
            color: 'danger',
          });
          await toast.present();
        },
      });
  }

  private async presentActionToast(approved: boolean, name: string): Promise<void> {
    const messageKey = approved
      ? marker('trip-finances.fundraising-profile-panel.approve-success')
      : marker('trip-finances.fundraising-profile-panel.reject-success');
    const toast = await this.toastController.create({
      message: this.transloco.translate(messageKey, { name }),
      duration: 4000,
      color: 'success',
    });
    await toast.present();
  }
}
